import { AlertTriangle } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";

interface SpcDataPoint {
  date: string;
  value: number;
  ucl: number | null;
  lcl: number | null;
  supplier: string | null;
  batch: string | null;
  passed: boolean | null;
}

interface SpcStats {
  mean: number;
  stdDev: number;
  ucl: number;
  lcl: number;
  min: number;
  max: number;
}

interface SpcTabProps {
  testParameters: string[];
  selectedParameter: string;
  onParameterChange: (value: string) => void;
  spcData: (SpcDataPoint | null)[];
  spcStats: SpcStats | null;
}

export function SpcTab({
  testParameters,
  selectedParameter,
  onParameterChange,
  spcData,
  spcStats,
}: SpcTabProps) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-lg">Statistical Process Control</CardTitle>
              <CardDescription>Monitor test parameters with control limits</CardDescription>
            </div>
            <Select value={selectedParameter} onValueChange={onParameterChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select parameter..." />
              </SelectTrigger>
              <SelectContent>
                {testParameters.map((param) => (
                  <SelectItem key={param} value={param}>
                    {param}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {selectedParameter && spcData.length > 0 ? (
            <>
              {/* SPC Stats */}
              {spcStats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Mean</p>
                    <p className="font-bold">{spcStats.mean.toFixed(3)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Std Dev</p>
                    <p className="font-bold">{spcStats.stdDev.toFixed(3)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-destructive/10">
                    <p className="text-xs text-muted-foreground">UCL</p>
                    <p className="font-bold text-destructive">{spcStats.ucl.toFixed(3)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-destructive/10">
                    <p className="text-xs text-muted-foreground">LCL</p>
                    <p className="font-bold text-destructive">{spcStats.lcl.toFixed(3)}</p>
                  </div>
                </div>
              )}

              {/* SPC Chart */}
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={spcData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} domain={["auto", "auto"]} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "var(--card)",
                        border: "1px solid var(--border)",
                        borderRadius: "8px",
                      }}
                      formatter={(value, name) => [
                        typeof value === "number" ? value.toFixed(3) : value,
                        name,
                      ]}
                    />
                    {spcStats && (
                      <>
                        <ReferenceLine
                          y={spcStats.ucl}
                          stroke="var(--destructive)"
                          strokeDasharray="5 5"
                          label={{ value: "UCL", fill: "var(--destructive)", fontSize: 10 }}
                        />
                        <ReferenceLine
                          y={spcStats.lcl}
                          stroke="var(--destructive)"
                          strokeDasharray="5 5"
                          label={{ value: "LCL", fill: "var(--destructive)", fontSize: 10 }}
                        />
                        <ReferenceLine
                          y={spcStats.mean}
                          stroke="var(--primary)"
                          strokeDasharray="3 3"
                          label={{ value: "Mean", fill: "var(--primary)", fontSize: 10 }}
                        />
                      </>
                    )}
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="var(--primary)"
                      strokeWidth={2}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        const isOutOfSpec =
                          spcStats &&
                          (payload.value > spcStats.ucl || payload.value < spcStats.lcl);
                        return (
                          <circle
                            cx={cx}
                            cy={cy}
                            r={isOutOfSpec ? 6 : 4}
                            fill={isOutOfSpec ? "var(--destructive)" : "var(--primary)"}
                          />
                        );
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="h-[400px] flex items-center justify-center text-muted-foreground">
              {testParameters.length === 0 ? (
                <div className="text-center">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No test parameters found in inspection data</p>
                </div>
              ) : (
                <p>Select a parameter to view SPC chart</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
